import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import { Icon } from "@iconify/react";

export default function EditRegistrationFormModel({ open, onClose, fieldId }) {
    const apiUrl = process.env.REACT_APP_API_BASE_URL;
    const bearerToken = localStorage.getItem('token');
    const [selectedType, setSelectedType] = useState();
    const [formImagePreview, setFormImagePreview] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [optionsArray, setOptionsArray] = useState(['']);
    const [fetchedFormFields, setFetchedFormFields] = useState([]);
    const modalRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                onClose();
            }
        };

        if (open) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [open, onClose]);

    const handleChange = (index, value) => {
        const updatedOptions = [...optionsArray];
        updatedOptions[index] = value; // Update the specific option
        setOptionsArray(updatedOptions);
    };

    const addOption = () => {
        setOptionsArray([...optionsArray, '']); // Add a new empty input
    };

    const deleteOption = (index) => {
        const updatedOptions = optionsArray.filter((_, i) => i !== index); // Remove the selected option
        setOptionsArray(updatedOptions);
    };

    const handleClose = () => {
        onClose();
        setSelectedType('');
        editFormField.resetForm();
        setFormImagePreview(null);
        setOptionsArray[''];
    };

    useEffect(() => {
        const fetchFormField = async () => {
            try {
                const response = await axios.get(`${apiUrl}event-service/registration-form/${fieldId}`);

                if (response.data.success && response.data.data) {
                    const fields = response.data.data;
                    editFormField.setValues({
                        name: fields.name,
                        type: fields.type,
                        isRequired: fields.isRequired,
                        eventId: fields.eventId,
                        formImage: fields.formImage,
                        regex: fields.regex,
                        showMobile: fields.showMobile,
                        dummyValue: fields.dummyValue || '',
                    });
                    setSelectedType(fields.type);
                    const optionsArray = fields.options ? fields.options.split(',').map(option => option.trim()) : [];
                    setOptionsArray(optionsArray);
                    setFetchedFormFields(fields);

                    if (fields.type === 'Image') {
                        setFormImagePreview(fields.formImage?.secure_url);
                    }
                }
            } catch (error) {
                console.error('Error fetching fields data:', error.message);
            }
        };
        fetchFormField();
    }, [fieldId, open]);

    const onSubmit = async (values, { resetForm }) => {
        setIsLoading(true);
        try {
            const formData = new FormData();
            if (editFormField.values.formImage) {
                formData.append('formImage', values.formImage);
            }
            if (fetchedFormFields?.name !== values.name) {
                formData.append('name', values.name);
            }
            formData.append('type', selectedType);
            formData.append('options', optionsArray);
            formData.append('isRequired', values.isRequired);
            formData.append('regex', values.regex);
            formData.append('showMobile', values.showMobile);
            formData.append('dummyValue', values.dummyValue);

            await editFormFieldData(formData);
            resetForm();
            onClose();
            setIsLoading(false);
        } catch (error) {
            console.error('Error submitting form:', error.message);
            setIsLoading(false);
        }
    };

    const editFormField = useFormik({
        initialValues: {
            name: '',
            type: '',
            options: '',
            formImage: '',
            isRequired: '',
            eventId: '',
            regex: '',
            showMobile: false,
            dummyValue: '',
        },
        validationSchema: Yup.object({
            name: Yup.string().required('Required'),
        }),
        onSubmit,
    });

    const editFormFieldData = async (values) => {
        try {
            const response = await axios.put(`${apiUrl}event-service/registration-form/${fieldId}`, values,
                { headers: { Authorization: `Bearer ${bearerToken}` } }
            );
            if (response.data.success) {
                setIsLoading(false);
                console.log('Data updated successfully:', data);
            }
        } catch (error) {
            console.error('Error updating data:', error.message);
        }
    };

    const handleImagePreview = (event) => {
        const file = event.currentTarget.files[0];
        editFormField.setFieldValue('formImage', file);
        setFormImagePreview(URL.createObjectURL(file));
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center p-4 sm:p-4 z-50">
            {/* Background overlay */}
            <div className="absolute inset-0 bg-black opacity-30" onClick={handleClose}></div>

            {/* Modal */}
            <div className="bg-white rounded-lg w-full max-w-xl z-50 relative max-h-[95vh]">
                {/* Header */}
                <div className="bg-blue-500 text-white py-3 px-6 sm:px-6 rounded-lg w-full sm:w-3/4 mx-auto relative z-10 -mt-6">
                    <h2 className="text-lg sm:text-xl font-semibold text-center">Edit Registration Form Fields</h2>
                </div>
                <div className="relative bg-white space-y-4 shadow-md rounded-b-lg">
                    {/* Dialog Content */}
                    <form onSubmit={editFormField.handleSubmit}>
                        <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
                            {/* Name Field */}
                            <div>
                                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                                    Name
                                </label>
                                <input
                                    id="name"
                                    name="name"
                                    type="text"
                                    value={editFormField.values.name}
                                    onChange={editFormField.handleChange}
                                    onBlur={editFormField.handleBlur}
                                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${editFormField.touched.name && editFormField.errors.name
                                        ? 'border-red-500'
                                        : 'border-gray-300'
                                        }`}
                                />
                                {editFormField.touched.name && editFormField.errors.name && (
                                    <p className="mt-1 text-sm text-red-600">{editFormField.errors.name}</p>
                                )}
                            </div>

                            {/* Type Select */}
                            <div>
                                <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-2">
                                    Select Type
                                </label>
                                <select
                                    id="type"
                                    required
                                    value={selectedType}
                                    onChange={(e) => setSelectedType(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white h-11"
                                >
                                    <option value="">Select Type</option>
                                    <option value="text">Text</option>
                                    <option value="number">Number</option>
                                    <option value="phone">Phone</option>
                                    <option value="email">Email</option>
                                    <option value="select">Drop Down Selection</option>
                                    <option value="radio">Single Choice</option>
                                    <option value="checkbox">Check Box</option>
                                    <option value="image">Image</option>
                                </select>
                            </div>

                            <div className='flex flex-col sm:flex-row sm:space-x-6 space-y-4 sm:space-y-0'>
                                {/* Required Switch */}
                                <div className="flex-1">
                                    <div className="flex items-center space-x-3">
                                        <span className="text-sm font-light text-gray-700">Required:</span>
                                        <label className="relative inline-flex items-center cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={editFormField.values.isRequired}
                                                onChange={(e) =>
                                                    editFormField.setFieldValue('isRequired', e.target.checked)
                                                }
                                                className="sr-only"
                                            />
                                            <div className={`w-11 h-6 rounded-full transition-colors ${editFormField.values.isRequired ? 'bg-blue-600' : 'bg-gray-200'
                                                }`}>
                                                <div className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform ${editFormField.values.isRequired ? 'translate-x-5' : 'translate-x-0.5'
                                                    } mt-0.5`}></div>
                                            </div>
                                        </label>
                                    </div>
                                </div>

                                {/* Mobile Switch */}
                                <div className="flex-1">
                                    <div className="flex items-center space-x-3">
                                        <span className="text-sm font-light text-gray-700">Show On Mobile:</span>
                                        <label className="relative inline-flex items-center cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={editFormField.values.showMobile}
                                                onChange={(e) =>
                                                    editFormField.setFieldValue('showMobile', e.target.checked)
                                                }
                                                className="sr-only"
                                            />
                                            <div className={`w-11 h-6 rounded-full transition-colors ${editFormField.values.showMobile ? 'bg-blue-600' : 'bg-gray-200'
                                                }`}>
                                                <div className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform ${editFormField.values.showMobile ? 'translate-x-5' : 'translate-x-0.5'
                                                    } mt-0.5`}></div>
                                            </div>
                                        </label>
                                    </div>
                                </div>
                            </div>

                            {/* Options for select, radio, checkbox */}
                            {selectedType && selectedType.length > 0 && (
                                <>
                                    {(selectedType === 'select' || selectedType === 'radio' || selectedType === 'checkbox') && (
                                        <div>
                                            <div className="space-y-3">
                                                {optionsArray.map((option, index) => (
                                                    <div key={index} className="flex items-center space-x-2">
                                                        <input
                                                            type="text"
                                                            placeholder={`Option ${index + 1}`}
                                                            name={`options[${index}]`}
                                                            value={option}
                                                            onChange={(e) => handleChange(index, e.target.value)}
                                                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                        />
                                                        <button
                                                            type="button"
                                                            onClick={addOption}
                                                            className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors touch-manipulation"
                                                        >
                                                            <Icon icon="material-symbols-light:add-rounded" className="w-6 h-6" />
                                                        </button>
                                                        {optionsArray.length > 1 && (
                                                            <button
                                                                type="button"
                                                                onClick={() => deleteOption(index)}
                                                                className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors touch-manipulation"
                                                            >
                                                                <Icon icon="material-symbols-light:remove" className="w-6 h-6" />
                                                            </button>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Image Upload */}
                                    {selectedType === 'image' && (
                                        <div>
                                            {formImagePreview && (
                                                <div className="flex justify-center items-center border border-dashed border-gray-300 rounded-md w-full sm:w-1/2 max-h-48 mb-4 h-24">
                                                    <img
                                                        src={formImagePreview}
                                                        alt="Image Preview"
                                                        className="w-full max-h-24 object-contain"
                                                    />
                                                </div>
                                            )}
                                            <label className="inline-flex items-center px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md cursor-pointer transition-colors">
                                                Upload Image
                                                <input
                                                    type="file"
                                                    hidden
                                                    accept="image/*"
                                                    onChange={handleImagePreview}
                                                    name="formImage"
                                                />
                                            </label>
                                        </div>
                                    )}
                                </>
                            )}

                            {editFormField.values.showMobile && editFormField.values.isRequired && (
                                <div>
                                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                                        Default Value
                                    </label>
                                    <input
                                        id="dummyValue"
                                        name="dummyValue"
                                        type="text"
                                        value={editFormField.values.dummyValue}
                                        onChange={editFormField.handleChange}
                                        onBlur={editFormField.handleBlur}
                                        className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${editFormField.touched.dummyValue && editFormField.errors.dummyValue
                                            ? 'border-red-500'
                                            : 'border-gray-300'
                                            }`}
                                    />
                                    {editFormField.touched.dummyValue && editFormField.errors.dummyValue && (
                                        <p className="mt-1 text-sm text-red-600">{editFormField.errors.dummyValue}</p>
                                    )}
                                </div>
                            )}

                        </div>

                        {/* Dialog Actions */}
                        <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 p-4 sm:p-6">
                            <button
                                type="button"
                                onClick={handleClose}
                                className="w-full sm:w-auto px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="w-full sm:w-auto px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                            >
                                {isLoading ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    )
}

EditRegistrationFormModel.propTypes = {
    open: PropTypes.isRequired,
    onClose: PropTypes.isRequired,
    fieldId: PropTypes.isRequired,
};